import React, {
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import { useIntl } from 'react-intl';

import {
  AppIcon,
} from '@folio/stripes/core';
import stripesFinalForm from '@folio/stripes/final-form';
import {
  AccordionSet,
  AccordionStatus,
  Col,
  Layer,
  Pane,
  Row,
  ExpandAllButton,
  LoadingPane,
} from '@folio/stripes/components';

import {
  NavigateBackPaneFirstMenu,
  FormPaneFooter,
  StyledFormPane,
} from '../../../../components/common';
import {
  APP_NAME,
  ICON_KEYS,
  FULL_PANE_WIDTH,
} from '../../../../constants';
import {
  GeneralInformation,
  AssignUsers,
} from './components';

import {
  EntityNotFound,
} from '../../../../routes/components';
import css from './team-form.css';

const TeamForm = ({
  form,
  pristine,
  submitting,
  handleSubmit,
  formTitle,
  onBack,
  isLoading,
  isFailed,
}) => {
  const {
    reset,
  } = form;
  const initialAccordionState = {
    general: true,
    assignedUsers: true,
  };

  const intl = useIntl();

  const getFirstMenu = useCallback(() => (
    <NavigateBackPaneFirstMenu onClickHandler={onBack} />
  ), [onBack]);

  const getFooter = useCallback(() => (
    <FormPaneFooter
      form={form}
      pristine={pristine}
      submitting={submitting}
      handleSubmit={handleSubmit}
      onCancel={reset}
    />
  ), [pristine, submitting, reset, handleSubmit, form]);

  const getAppIcon = useCallback(() => (
    <AppIcon
      app={APP_NAME}
      iconKey={ICON_KEYS.APP}
    />
  ), []);

  if (isLoading) {
    return (
      <Layer
        isOpen
        contentLabel={intl.formatMessage({ id: 'ui-task-list.team.contentLabel.loadingPane' })}
      >
        <LoadingPane defaultWidth={FULL_PANE_WIDTH} />
      </Layer>
    );
  }

  if (isFailed) {
    return (
      <Layer
        isOpen
        contentLabel={intl.formatMessage({ id: 'ui-task-list.team.contentLabel.errorPane' })}
      >
        <EntityNotFound
          errorTextTranslationKey="ui-task-list.error.notFound.team"
          pageTitleTranslationKey="ui-task-list.title.edit.notFound"
          onBack={onBack}
        />
      </Layer>
    );
  }

  return (
    <Layer
      isOpen
      contentLabel={intl.formatMessage({ id: 'ui-task-list.team.contentLabel.formPane' })}
    >
      <form className={css.teamForm}>
        <Pane
          dismissible
          appIcon={getAppIcon()}
          defaultWidth={FULL_PANE_WIDTH}
          firstMenu={getFirstMenu()}
          footer={getFooter()}
          paneTitle={formTitle()}
          onClose={reset}
        >
          <StyledFormPane>
            <AccordionStatus>
              <Row end="xs">
                <Col xs>
                  <ExpandAllButton />
                </Col>
              </Row>
              <AccordionSet initialStatus={initialAccordionState}>
                <GeneralInformation />
                <AssignUsers />
              </AccordionSet>
            </AccordionStatus>
          </StyledFormPane>
        </Pane>
      </form>
    </Layer>
  );
};

TeamForm.propTypes = {
  formTitle: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  onBack: PropTypes.func.isRequired,
  form: PropTypes.shape({
    change: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
  }),
  isFailed: PropTypes.bool,
  isLoading: PropTypes.bool,
  values: PropTypes.shape({
    members: PropTypes.array,
  }),
};

TeamForm.defaultProps = {
  isFailed: false,
  isLoading: false,
};

export default stripesFinalForm({
  initialValuesEqual: (a, b) => isEqual(a, b),
  navigationCheck: true,
  subscription: {
    pristine: true,
    submitting: true,
    values: true,
  },
})(TeamForm);
